import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;

/**
 * Live queue hook used by every authenticated role view.
 * Polls /api/queue/poll every 5s so all 5 personas stay in sync.
 *
 * Returns:
 *  - patients: list of PatientQueue
 *  - loading, error, lastUpdated
 *  - actions: addPatient, updatePatient, deletePatient, addEmergency, seed
 *  - refresh(): forces an immediate refetch
 */
export const useQueue = ({ token, enabled = true } = {}) => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;

  const fetchOnce = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/queue/poll`, { headers: authHeaders });
      setPatients(res.data.patients || []);
      setLastUpdated(res.data.last_updated);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!enabled || !token) return;
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [enabled, token, fetchOnce]);

  const addPatient = async (data) => {
    const res = await axios.post(`${API}/queue`, data, { headers: authHeaders });
    await fetchOnce();
    return res.data;
  };

  const addEmergency = async (data) => {
    const res = await axios.post(
      `${API}/queue`,
      { ...data, is_emergency: true, entry_method: 'Emergency' },
      { headers: authHeaders }
    );
    await fetchOnce();
    return res.data;
  };

  const updatePatient = async (id, patch) => {
    const res = await axios.patch(`${API}/queue/${id}`, patch, { headers: authHeaders });
    await fetchOnce();
    return res.data;
  };

  const deletePatient = async (id) => {
    await axios.delete(`${API}/queue/${id}`, { headers: authHeaders });
    await fetchOnce();
  };

  const seed = async () => {
    await axios.post(`${API}/queue/seed`);
    await fetchOnce();
  };

  return {
    patients,
    loading,
    error,
    lastUpdated,
    refresh: fetchOnce,
    addPatient,
    addEmergency,
    updatePatient,
    deletePatient,
    seed,
  };
};

export default useQueue;
